"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import AnchorNav, { type AnchorNavItem } from "./AnchorNav";

export type SectionGroupItem = {
  number: string;
  label: string;
  short: string;
  anchor: string;
  dark: boolean;
};

type Props = {
  /** Rows of "number", "label", "short", "anchor" and "dark". */
  items: SectionGroupItem[];
  /** Accessible name for the navbar. */
  navLabel: string;
  /** Whether to draw the edge rail. */
  showRail: boolean;
  children: React.ReactNode;
};

type NavState = "top" | "pinned" | "ended";

/**
 * The port of partials/section-group-open.php and section-group-close.php, with
 * the grouped half of anchor-nav.js.
 *
 * The bracket a run of page-builder sections is wrapped in when a Group Start and
 * a Group End are both present. It gives the run a navbar of its own and a rail of
 * dashes down the left edge, both reading from the same list of items.
 *
 * The navbar is the anchor-nav fragment, so the group inherits the bar the charter
 * already uses; it is told to leave its own rail out, since the group draws one.
 * Both the bar and the rail are marked up as plain jump links, so they work before
 * the script runs; the script only pins the bar to the group and shows the rail
 * while the page is inside it.
 *
 * The bar rests at the top of the group until the page has scrolled past it,
 * travels under the header through the group, and parks at the bottom. That is
 * why it is not position: sticky — it has to stop at the bottom of the group.
 */
export default function SectionGroup({
  items: allItems,
  navLabel,
  showRail: railWanted,
  children,
}: Props) {
  const items = allItems.filter((item) => item.anchor);
  const showRail = railWanted && items.length > 0;

  const navItems: AnchorNavItem[] = items.map((item) => ({
    number: item.number,
    label: item.short || item.label,
    anchor: item.anchor,
    dark: item.dark,
  }));

  const groupRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const metrics = useRef({
    topSpacing: 0,
    navHeight: 0,
    groupTop: 0,
    groupBottom: 0,
  });

  const [enhanced, setEnhanced] = useState(false);
  const [navState, setNavState] = useState<NavState>("top");
  const [inView, setInView] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);

  const anchors = items.map((item) => item.anchor).join(" ");

  /**
   * Where the page has to sit for a section to start just under the bar, or
   * null when nothing on the page carries that id.
   */
  const offsetFor = (target: string) => {
    const element = document.getElementById(target);
    const { topSpacing, navHeight } = metrics.current;

    return element
      ? Math.max(
          0,
          element.getBoundingClientRect().top +
            window.scrollY -
            topSpacing -
            navHeight,
        )
      : null;
  };

  useEffect(() => {
    const group = groupRef.current;
    const navbar = navbarRef.current;
    const inner = innerRef.current;
    const targets = anchors ? anchors.split(" ") : [];

    if (!group || !navbar || !inner || !targets.length) {
      return;
    }

    const header = document.getElementById("site-header");

    /**
     * The header owns these, so they are read off it rather than off :root, and
     * on every measure: the admin bar and the page-nav row change its height.
     */
    const headerMetric = (name: string) =>
      header
        ? parseInt(getComputedStyle(header).getPropertyValue(name), 10) || 0
        : 0;

    /**
     * Re-read the room the header takes, how tall the bar is and where the group
     * begins and ends. The bar is measured out of the travelling states or it
     * measures a fixed element, and the group is measured after the body has been
     * given the room to hold for it, or it comes back short by the bar's height.
     */
    const measure = () => {
      // React owns data-nav; it is only borrowed here and handed back unchanged before measure returns.
      const state = navbar.dataset.nav ?? "top";

      navbar.dataset.nav = "top";

      const topSpacing =
        headerMetric("--header-sticky-height") +
        headerMetric("--admin-bar-height");
      const navHeight = inner.offsetHeight;

      group.style.setProperty("--group-nav-top", `${topSpacing}px`);
      group.style.setProperty("--group-nav-height", `${navHeight}px`);

      const groupTop = group.getBoundingClientRect().top + window.scrollY;

      metrics.current = {
        topSpacing,
        navHeight,
        groupTop,
        groupBottom: groupTop + group.offsetHeight,
      };

      navbar.dataset.nav = state;
    };

    /**
     * Place the bar, show or hide the rail, and mark the section the page is in.
     * The rail belongs to the group rather than the page, so it only shows while
     * the middle of the viewport is somewhere inside the group.
     */
    const refresh = () => {
      const scrollTop = window.scrollY;
      const { topSpacing, navHeight, groupTop, groupBottom } = metrics.current;
      const pinFrom = groupTop - topSpacing;
      const pinTo = Math.max(pinFrom, groupBottom - topSpacing - navHeight);

      setNavState(
        scrollTop < pinFrom ? "top" : scrollTop > pinTo ? "ended" : "pinned",
      );

      const middle = scrollTop + window.innerHeight / 2;

      setInView(middle >= groupTop && middle <= groupBottom);

      // One counts as current once its top has passed under the bar, so the last to do so wins.
      let active: string | null = null;

      targets.forEach((target) => {
        const at = offsetFor(target);

        if (at !== null && scrollTop >= at - 2) {
          active = target;
        }
      });

      setCurrent(active);
    };

    const onResize = () => {
      measure();
      refresh();
    };

    setEnhanced(true);
    measure();
    refresh();

    // Anything above the group sizes off svh, so re-measure once it has settled.
    window.addEventListener("load", onResize);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", refresh, { passive: true });

    return () => {
      window.removeEventListener("load", onResize);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", refresh);
    };
  }, [anchors]);

  const jump = (target: string) => {
    const at = offsetFor(target);

    if (at !== null) {
      window.scrollTo({ top: at, behavior: "smooth" });
    }
  };

  // The rail inverts whenever the section the page is in is a dark one.
  const overDark = items.some((item) => item.anchor === current && item.dark);

  return (
    <div
      ref={groupRef}
      // js-section-group is the theme's script hook, kept by name.
      className="group/sections js-section-group relative"
      data-enhanced={enhanced}
      data-in-view={enhanced && inView}
    >
      {/*
       * Out of the flow for its whole life: it rests at the top of the group, goes fixed
       * under the header while the page is inside the group, then parks at the bottom.
       * The travelling states only exist once the script runs.
       */}
      <div
        ref={navbarRef}
        className={clsx(
          "absolute top-0 right-0 left-0 z-5",
          "data-[nav=pinned]:fixed data-[nav=pinned]:top-[var(--group-nav-top,0px)]",
          "data-[nav=ended]:top-auto data-[nav=ended]:bottom-0",
        )}
        data-nav={enhanced ? navState : "top"}
      >
        <AnchorNav
          items={navItems}
          showRail={false}
          navLabel={navLabel}
          current={current}
          enhanced={enhanced}
          innerRef={innerRef}
          onJump={enhanced ? jump : undefined}
        />
      </div>

      {showRail && (
        <div
          // No room beside the content until the viewport is wide, and nothing to mark until the page is inside the group.
          className="fixed top-1/2 left-6 z-6 hidden -translate-y-1/2 flex-col gap-2.5 above-small-desktop:group-data-[in-view=true]/sections:flex"
          aria-hidden="true"
        >
          {items.map((item) => {
            const active = item.anchor === current;

            return (
              <span
                key={item.anchor}
                className="flex items-center gap-2 py-[3px] group-data-[enhanced=true]/sections:cursor-pointer"
                data-target={item.anchor}
                data-dark={item.dark ? "1" : "0"}
                title={item.label}
                onClick={enhanced ? () => jump(item.anchor) : undefined}
              >
                {/* The whole rail inverts over a dark section; the active mark is green either way. */}
                <span
                  className={clsx(
                    "h-0.5 shrink-0 rounded-[1px] transition-[width,background-color] duration-400 ease-[ease] motion-reduce:transition-none",
                    active
                      ? "w-4.5 bg-das-green"
                      : clsx(
                          "w-2",
                          overDark ? "bg-white/28" : "bg-brand-blue/20",
                        ),
                  )}
                ></span>

                <span
                  className={clsx(
                    "text-[11px] leading-none font-bold tracking-[0.1em] whitespace-nowrap",
                    "transition-[opacity,translate,color] duration-400 ease-[ease] motion-reduce:transition-none",
                    active
                      ? "translate-x-0 opacity-100"
                      : "-translate-x-1 opacity-0",
                    overDark ? "text-white" : "text-brand-blue",
                  )}
                >
                  {item.number}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {/* Holds the room for the out-of-flow bar; the fallback covers the frame before it is measured. */}
      <div className="pt-[var(--group-nav-height,56px)]">{children}</div>
    </div>
  );
}
